import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { BreachCode, BreachSeverity } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { ChipComponent } from './chip.component';
import { BREACH_LABEL_KEY, ChipTone, SEVERITY_TONE } from './guarantee-vocabulary';
import { PanelSectionComponent } from './panel-section.component';

/**
 * A finding as this section needs it. Callers map their own shape onto it: the analyser reports
 * findings against a guarantee and against a line, and only the guarantee's carry their own severity.
 */
export interface RecordBreachView {
  key: string;
  code: BreachCode;
  detectedAt: string;
  severity?: BreachSeverity;
}

/**
 * Everything the analyser found against one record, as its own section — the counterpart to
 * {@link BreachListComponent}, which lists findings across the whole window as rows to work down.
 * Renders nothing at all when there are none, so a clean record costs no heading and no gap.
 */
@Component({
  selector: 'guarantees-record-breaches',
  standalone: true,
  imports: [DatePipe, TranslatePipe, ChipComponent, PanelSectionComponent],
  // A record with no findings must add no gap to the panel, which an empty host box would.
  host: { class: 'contents' },
  template: `
    @if (breaches().length > 0) {
      <guarantees-panel-section [label]="'guaranteeV2.label.breaches' | translate" [hint]="'guaranteeV2.message.breachesHint' | translate">
        <div class="flex flex-wrap gap-1">
          @for (breach of breaches(); track breach.key) {
            <guarantees-chip
              [tone]="toneOf(breach)"
              icon="rule"
              [tooltip]="'guaranteeV2.message.breachDetectedHint' | translate: { date: breach.detectedAt | date: 'dd.MM.yy HH:mm' }"
              >{{ titleKeyOf(breach) | translate }}</guarantees-chip
            >
          }
        </div>
      </guarantees-panel-section>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordBreachesComponent {
  breaches = input.required<RecordBreachView[]>();

  /** A code this build does not know still reads as something to look at, never as nothing. */
  protected toneOf(breach: RecordBreachView): ChipTone {
    return (breach.severity && SEVERITY_TONE[breach.severity]) || 'warning';
  }

  protected titleKeyOf(breach: RecordBreachView): string {
    return BREACH_LABEL_KEY[breach.code] ?? '';
  }
}
